import type { ReactElement } from 'react'
import { Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { RequireAuth } from './auth/RequireAuth'
import { StoreProvider } from './store/StoreContext'
import { RequireStore } from './store/RequireStore'
import { UnsavedChangesProvider } from './layout/UnsavedChangesContext'
import { AppShell } from './layout/AppShell'
import { LoginPage } from './pages/LoginPage'
import { RequestPasswordResetPage } from './pages/RequestPasswordResetPage'
import { ResetPasswordPage } from './pages/ResetPasswordPage'
import { ProposalPage } from './pages/proposal/ProposalPage'
import { OrderHistoryPage } from './pages/orders/OrderHistoryPage'
import { OrderDetailPage } from './pages/orders/OrderDetailPage'
import {
  DEFAULT_STORE_PAGE,
  FORGOT_PASSWORD_PATH,
  HOME_PATH,
  LOGIN_PATH,
  ORDER_DETAIL_PATTERN,
  ORDER_PAGE,
  PROPOSAL_PAGE,
  RESET_PASSWORD_PATH,
  storePageBasePath,
  storePagePattern,
} from './lib/routes'
import type { StorePage } from './lib/routes'

/**
 * One entry of the route table: what react-router needs to answer a URL, under
 * a name. Nothing here builds URLs — a component links with the builders in
 * `lib/routes` and never reaches into this table.
 */
const routeEntry = (path: string, element: ReactElement) => ({
  routeProps: { path, element },
})

/**
 * A store-scoped page (the page segment, then `:storeCode`). Its pattern comes
 * out of `lib/routes`, the same module the app's links are built from, so the
 * URL a page navigates to and the one the router matches are the same string
 * with a different second segment.
 *
 * The entry wraps the page in its own `RequireStore` rather than taking the
 * wrapper ready-made: the guard's `page` and the path's page must agree, and
 * naming the page twice at the call site is a mismatch `tsc` cannot see (both
 * spellings type-check).
 */
const storeRouteEntry = (page: StorePage, pageElement: ReactElement) => ({
  routeProps: {
    path: storePagePattern(page),
    element: <RequireStore page={page}>{pageElement}</RequireStore>,
  },
})

/**
 * The bare page URL (`/propuesta`, no store): renders nothing of its own, it
 * only forwards to the default store on that same page.
 */
const storeRedirectEntry = (page: StorePage) => ({
  routeProps: { path: storePageBasePath(page), element: <RequireStore page={page} /> },
})

/**
 * RoutesMap — central, typed route table (mirrors the admin's `routes-map.tsx`):
 * every path the SPA answers to is named here once, paired with the element that
 * answers it. `clientRoutes` below is the only place the tree shape is written.
 *
 * `protected` routes are children of ONE layout route (RequireAuth +
 * StoreProvider + AppShell) — no per-route guard wrapper. The store is part of
 * the URL, so it is deep-linkable and navigable with back/forward; each
 * store-scoped route is wrapped in RequireStore, which redirects a missing or
 * invalid code to the user's default store. The bare page routes (no code)
 * reuse RequireStore with no children to bounce straight there.
 *
 * The table is read for its `routeProps` and for nothing else: a page does not
 * import it to build its links. That would close a module cycle, and
 * `clientRoutes` below creates its elements at module scope, so the cycle would
 * blow up rather than resolve quietly. The builders and the fixed paths live in
 * `lib/routes`, which imports nothing, and this table is built from those same
 * definitions, so the URL a page links to and the pattern the router matches
 * still come from one place.
 */
export const RoutesMap = {
  public: {
    login: routeEntry(LOGIN_PATH, <LoginPage />),
    forgotPassword: routeEntry(FORGOT_PASSWORD_PATH, <RequestPasswordResetPage />),
    resetPassword: routeEntry(RESET_PASSWORD_PATH, <ResetPasswordPage />),
  },
  protected: {
    // `/` renders no page of its own — RequireStore forwards it to the
    // last-used store's default page.
    home: routeEntry(HOME_PATH, <RequireStore page={DEFAULT_STORE_PAGE} />),
    propuestaRedirect: storeRedirectEntry(PROPOSAL_PAGE),
    pedidosRedirect: storeRedirectEntry(ORDER_PAGE),
    propuesta: storeRouteEntry(PROPOSAL_PAGE, <ProposalPage />),
    pedidos: storeRouteEntry(ORDER_PAGE, <OrderHistoryPage />),
    pedidoDetalle: {
      routeProps: {
        path: ORDER_DETAIL_PATTERN,
        element: (
          <RequireStore page={ORDER_PAGE}>
            <OrderDetailPage />
          </RequireStore>
        ),
      },
    },
  },
} as const

/**
 * The route tree handed to `createBrowserRouter` (react-router data mode, no
 * loaders for now). Replaces the old declarative `<Routes>` inside a catch-all
 * `/*` route: the router now knows every path up front.
 */
export const clientRoutes: Array<RouteObject> = [
  RoutesMap.public.login.routeProps,
  RoutesMap.public.forgotPassword.routeProps,
  RoutesMap.public.resetPassword.routeProps,
  {
    element: (
      <RequireAuth>
        <StoreProvider>
          <UnsavedChangesProvider>
            <AppShell />
          </UnsavedChangesProvider>
        </StoreProvider>
      </RequireAuth>
    ),
    children: [
      RoutesMap.protected.home.routeProps,
      RoutesMap.protected.propuestaRedirect.routeProps,
      RoutesMap.protected.propuesta.routeProps,
      RoutesMap.protected.pedidosRedirect.routeProps,
      RoutesMap.protected.pedidos.routeProps,
      RoutesMap.protected.pedidoDetalle.routeProps,
    ],
  },
  // Catch-all: send unknown paths home (the guard redirects to /login if needed)
  { path: '*', element: <Navigate to={HOME_PATH} replace /> },
]
